import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, Mail, Phone, MapPin, ChevronRight, Upload, Download, ImagePlus, Loader2, Tags, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { TagChip, TagPicker } from "@/components/TagPicker";
import ManageTagsDialog from "@/components/ManageTagsDialog";

import CustomerFormDialog from "@/components/CustomerFormDialog";
import DeleteCustomerDialog from "@/components/DeleteCustomerDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (p) => `${API}/files/${p}?auth=${localStorage.getItem("nlc_token")}`;
const statusLabel = { active: "Currently being serviced", inactive: "Not being serviced" };

export default function Customers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delId, setDelId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [photoBusyId, setPhotoBusyId] = useState(null);
  const [tagLib, setTagLib] = useState([]);
  const [tagFilter, setTagFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name-asc");
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const { isAdmin } = useAuth();
  const tagById = Object.fromEntries(tagLib.map((t) => [t.id, t]));
  const loadTags = () => api.get("/customer-tags").then((r) => setTagLib(r.data)).catch(() => {});

  const toggleTag = async (c, tagId) => {
    const current = c.tags || [];
    const next = current.includes(tagId) ? current.filter((t) => t !== tagId) : [...current, tagId];
    setItems((arr) => arr.map((x) => (x.id === c.id ? { ...x, tags: next } : x)));
    try { await api.put(`/customers/${c.id}/tags`, { tags: next }); }
    catch (e) { toast.error("Could not update tags"); load(); }
  };

  const toggleStatus = async (c) => {
    const next = (c.status || "active") === "active" ? "inactive" : "active";
    setItems((arr) => arr.map((x) => (x.id === c.id ? { ...x, status: next } : x)));
    try { await api.put(`/customers/${c.id}/status`, { status: next }); toast.success(statusLabel[next]); }
    catch (e) { toast.error("Could not update status"); load(); }
  };
  const photoTargetRef = useRef(null);
  const photoInputRef = useRef(null);

  const pickPhoto = (c) => { photoTargetRef.current = c; photoInputRef.current?.click(); };

  const uploadPhoto = async (file) => {
    const c = photoTargetRef.current;
    if (!file || !c) return;
    setPhotoBusyId(c.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const payload = { ...c, photo: data.path };
      delete payload.id; delete payload.created_at;
      await api.put(`/customers/${c.id}`, payload);
      toast.success("Property photo updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setPhotoBusyId(null); photoTargetRef.current = null; }
  };

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported ${data.created} customers (${data.skipped} skipped)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const downloadTemplate = () => {
    const csv = "name,email,phone,address,city,state,zip,property size,notes\nJane Doe,jane@email.com,864-555-0100,123 Main St,Simpsonville,SC,29681,0.25 acre,Gate code 1234\n";
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "customer_import_template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const load = () => api.get("/customers").then((r) => setItems(r.data));
  useEffect(() => { load(); loadTags(); }, []);

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setOpen(true); };
  const onSaved = (data, isNew) => {
    if (isNew) { setQ(""); setStatusFilter("all"); setTagFilter("all"); navigate(`/customers/${data.id}`); }
    else load();
  };

  const filtered = items.filter((c) => {
    const term = q.toLowerCase().trim();
    const hay = [c.name, c.email, c.phone, c.address, c.city, c.state, c.zip_code].filter(Boolean).join(" ").toLowerCase();
    const matchesQ = !term || hay.includes(term);
    const matchesTag = tagFilter === "all" || (c.tags || []).includes(tagFilter);
    const matchesStatus = statusFilter === "all" || (c.status || "active") === statusFilter;
    return matchesQ && matchesTag && matchesStatus;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "name-asc") return (a.name || "").localeCompare(b.name || "");
    if (sortBy === "name-desc") return (b.name || "").localeCompare(a.name || "");
    if (sortBy === "newest") return (b.created_at || "").localeCompare(a.created_at || "");
    if (sortBy === "oldest") return (a.created_at || "").localeCompare(b.created_at || "");
    return 0;
  });

  return (
    <div className="space-y-6" data-testid="customers-page">
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="customer-photo-input"
        onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }}
      />
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="customer-search"
            placeholder="Search name, address, phone, city…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[150px]" data-testid="sort-select">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name-asc" data-testid="sort-name-asc">Name A–Z</SelectItem>
              <SelectItem value="name-desc" data-testid="sort-name-desc">Name Z–A</SelectItem>
              <SelectItem value="newest" data-testid="sort-newest">Newest first</SelectItem>
              <SelectItem value="oldest" data-testid="sort-oldest">Oldest first</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]" data-testid="status-filter-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="status-filter-all">All statuses</SelectItem>
              <SelectItem value="active" data-testid="status-filter-active">Currently being serviced</SelectItem>
              <SelectItem value="inactive" data-testid="status-filter-inactive">Not being serviced</SelectItem>
            </SelectContent>
          </Select>
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger className="w-[170px]" data-testid="tag-filter-select">
              <SelectValue placeholder="Filter by tag" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="tag-filter-all">All tags</SelectItem>
              {tagLib.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`tag-filter-${t.id}`}>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.color }} />{t.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isAdmin && (
            <Button variant="outline" data-testid="manage-tags-btn" onClick={() => setManageTagsOpen(true)}>
              <Tags className="h-4 w-4 mr-1.5" /> Manage Tags
            </Button>
          )}
          <Button variant="outline" data-testid="import-customers-btn" onClick={() => { setImportResult(null); setImportOpen(true); }}>
            <Upload className="h-4 w-4 mr-1.5" /> Import CSV
          </Button>
          <Button data-testid="add-customer-btn" onClick={openNew} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">
            <Plus className="h-4 w-4 mr-1.5" /> Add Customer
          </Button>
        </div>
      </div>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle>Import customers from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Upload a CSV exported from your other CRM. We auto-match common column names (name, email, phone, address, city, state, zip, notes). Duplicates (same email) are skipped.</p>
            <button onClick={downloadTemplate} data-testid="download-template-btn" className="text-sm font-semibold text-primary flex items-center gap-1.5 hover:underline"><Download className="h-4 w-4" />Download template CSV</button>
            <label className="block border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors">
              <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
              <span className="text-sm font-medium">{importing ? "Importing…" : "Click to choose a .csv file"}</span>
              <input type="file" accept=".csv,text/csv" className="hidden" data-testid="import-file-input" disabled={importing} onChange={(e) => { doImport(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            {importResult && (
              <div className="rounded-lg bg-muted p-3 text-sm" data-testid="import-result">
                <p className="font-semibold text-primary">{importResult.created} added · {importResult.skipped} skipped</p>
                {(importResult.errors || []).length > 0 && <ul className="mt-1 text-xs text-destructive list-disc pl-4">{importResult.errors.map((er, i) => <li key={i}>{er}</li>)}</ul>}
              </div>
            )}
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setImportOpen(false)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {sorted.length === 0 ? (
        <Card className="p-12 text-center border-border border-dashed">
          <p className="text-muted-foreground">No customers yet. Add your first customer to get started.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map((c) => (
            <Card
              key={c.id}
              data-testid="customer-card"
              onClick={() => navigate(`/customers/${c.id}`)}
              className="overflow-hidden p-5 border-border rounded-lg hover:-translate-y-1 hover:shadow-lg transition-transform duration-200 bg-card cursor-pointer"
            >
              <div className="-mx-5 -mt-5 mb-4 h-28 overflow-hidden bg-muted relative group/photo">
                {c.photo ? (
                  <img
                    src={fileUrl(c.photo)}
                    alt="Property"
                    loading="lazy"
                    className="w-full h-full object-cover"
                    data-testid="customer-house-photo"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground" data-testid="customer-photo-placeholder">
                    <ImagePlus className="h-6 w-6 mb-1 opacity-60" />
                    <span className="text-xs font-medium">Add property photo</span>
                  </div>
                )}
                <button
                  data-testid="customer-photo-upload-btn"
                  onClick={(e) => { e.stopPropagation(); pickPhoto(c); }}
                  disabled={photoBusyId === c.id}
                  className="absolute inset-0 flex items-center justify-center bg-black/45 text-white text-xs font-semibold opacity-0 group-hover/photo:opacity-100 transition-opacity duration-200 disabled:opacity-100"
                >
                  {photoBusyId === c.id ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <span className="flex items-center gap-1.5"><ImagePlus className="h-4 w-4" />{c.photo ? "Change photo" : "Upload photo"}</span>
                  )}
                </button>
              </div>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <h3 className="font-display font-bold tracking-tight truncate flex items-center gap-1">{c.name}<ChevronRight className="h-4 w-4 text-muted-foreground" /></h3>
                  <button
                    type="button"
                    data-testid="customer-status-toggle"
                    onClick={(e) => { e.stopPropagation(); toggleStatus(c); }}
                    title="Click to toggle active / inactive"
                  >
                    <Badge
                      variant="outline"
                      className={`mt-1.5 text-[0.65rem] cursor-pointer transition-colors ${(c.status || "active") === "active" ? "border-primary/30 text-primary hover:bg-primary/10" : "text-muted-foreground hover:bg-muted"}`}
                    >
                      {statusLabel[c.status || "active"]}
                    </Badge>
                  </button>
                </div>
                <div className="flex gap-1">
                  <button data-testid="edit-customer-btn" onClick={(e) => { e.stopPropagation(); openEdit(c); }} className="p-2 rounded-md hover:bg-muted transition-colors duration-200">
                    <Pencil className="h-4 w-4 text-muted-foreground" />
                  </button>
                  <button data-testid="delete-customer-btn" onClick={(e) => { e.stopPropagation(); setDelId(c); }} className="p-2 rounded-md hover:bg-destructive/10 transition-colors duration-200">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </button>
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                {c.email && <div className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 shrink-0" />{c.email}</div>}
                {c.phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 shrink-0" />{c.phone}</div>}
                {(c.address || c.city) && (
                  <div className="flex items-center gap-2 truncate">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    {[c.address, c.city, c.state].filter(Boolean).join(", ")}
                  </div>
                )}
              </div>
              {c.property_size && (
                <div className="mt-3 text-xs bg-muted rounded-md px-2.5 py-1 inline-block">Lot: {c.property_size}</div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid={`customer-tags-${c.id}`}>
                {(c.tags || []).map((tid) => tagById[tid] && (
                  <TagChip key={tid} tag={tagById[tid]} onRemove={(id) => toggleTag(c, id)} />
                ))}
                <TagPicker
                  library={tagLib}
                  selected={c.tags || []}
                  onToggle={(id) => toggleTag(c, id)}
                  triggerTestId={`tag-picker-${c.id}`}
                />
              </div>
            </Card>
          ))}
        </div>
      )}

      <CustomerFormDialog open={open} onOpenChange={setOpen} customer={editing} onSaved={onSaved} />
      <DeleteCustomerDialog customer={delId} onOpenChange={(v) => !v && setDelId(null)} onDeleted={load} />

      <ManageTagsDialog
        open={manageTagsOpen}
        onOpenChange={setManageTagsOpen}
        library={tagLib}
        onChanged={loadTags}
      />
    </div>
  );
}
